/*
License

Copyright (c) 2022 Chukwubuikem Ume-Ugwa

Permission is hereby witheld, any person obtaining a copy
of this software and associated documentation files (the "Software"), cannot
use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software. These restrictions can be only lifted with
a written permission from Chukwubuikem Ume-Ugwa.
*/
import {
  Modal,
  ModalBody,
  useToast,
  ModalOverlay,
  ModalContent,
  Button,
  ModalFooter,
  Input,
  Center,
  VStack,
  SimpleGrid,
  GridItem,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  StatHelpText,
  Select,
  FormControl,
  FormErrorMessage,
  HStack,
  Checkbox,
  Alert,
  ModalCloseButton,
} from '@chakra-ui/react';
import React, { useCallback, useState, useRef, useEffect } from 'react';
import Schemas, { Constraints } from '../api/schemas';
import { parseBlob } from 'music-metadata-browser';
import { useStorage } from '../api/cache';
import Constants from '../constants';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import {
  createAssociatedTokenAccount,
  getAccountInfo,
  MINT_ADDRESS,
} from '../api/qdot';
import { PublicKey } from '@solana/web3.js';
import { firebaseFacade } from '../api/firebase';
import { log, MISTERY_WALLET } from '../utils';
import { getAssociatedTokenAddress } from '@solana/spl-token';
import { indexer } from '../api/indexing';

const MEGA_BYTES_PER_BYTE = 1_000_000;

const convertBytesToMB = bytes => Math.round(bytes / MEGA_BYTES_PER_BYTE);
const convertObjectToArray = object =>
  Object.keys(object).map(key => object[key]);

async function parseFile(file) {
  log('Parsing file');
  return parseBlob(file, { native: true, duration: true });
}

function TrackPreview({
  filename,
  fileSize,
  unit,
  onPriceChange,
  onUnitChange,
  onRemove,
  onAttest,
}) {
  const [invalidPrice, setInvalidPrice] = useState(false);
  const { publicKey } = useWallet();
  const handlePriceChange = ({ target: { value } }) => {
    const price = Number.parseFloat(value);
    setInvalidPrice(price < 0 || (price > 0 && price < 1e-8));
    onPriceChange(price, filename);
  };

  const handlePricingUnitChange = ({ target: { value } }) => {
    onUnitChange(Number.parseInt(value), filename);
  };

  const handleRemove = () => {
    onRemove(filename);
  };

  const handleCheckChanged = ({ target: { checked } }) => {
    onAttest(checked ? 1 : -1);
  };

  return (
    <VStack marginTop={[2, 5, 10]}>
      <HStack>
        <Alert status="warning" className="rounded-pill">
          Please attest that you own the rights to this file by checking the
          box.
        </Alert>
        <Checkbox
          aria-label="attestation-checkbox"
          border={'#1A202C'}
          borderWidth={'thick'}
          size="lg"
          colorScheme={'yellow'}
          onChange={handleCheckChanged}
        />
      </HStack>
      <StatGroup>
        <Stat>
          <StatLabel>Filename</StatLabel>
          <StatHelpText>{filename}</StatHelpText>
        </Stat>
        <Stat>
          <StatLabel>Size</StatLabel>
          <StatNumber>
            {fileSize} {unit}
          </StatNumber>
        </Stat>
        {publicKey && (
          <>
            <Stat>
              <StatLabel>Price</StatLabel>
              <FormControl isInvalid={invalidPrice}>
                <Input
                  width={[110]}
                  type={'number'}
                  onChange={handlePriceChange}
                  placeholder={0.00000001}
                />
                {invalidPrice && (
                  <FormErrorMessage>Invalid price</FormErrorMessage>
                )}
              </FormControl>
              <StatHelpText>QDOT</StatHelpText>
            </Stat>
            <Stat marginStart={[2]} marginEnd={[2]}>
              <StatLabel>Unit</StatLabel>
              <Select
                width={[125]}
                placeholder="Select Unit"
                defaultValue={0}
                onChange={handlePricingUnitChange}
              >
                <option value={0}>LOOPS</option>
                <option value={1}>SECONDS</option>
              </Select>
            </Stat>
          </>
        )}
        <Stat>
          <Button onClick={handleRemove} size="xs" ml="20px" bg={'transparent'}>
            <span class="material-icons">delete</span>
          </Button>
        </Stat>
      </StatGroup>
    </VStack>
  );
}

export default function TrackUpload(props) {
  const { isOpen, onClose, onUploadStart, onFinished, queue } = props;
  const fileInputRef = useRef();
  const linkInputRef = useRef();
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState({});
  const [link, setLink] = useState();

  const [currentPrice, setPrice] = useState({});
  const [currentPriceUnit, setPriceUnit] = useState({});
  const [show, setShow] = useState(false);

  const [opening, setOpening] = useState(false);


  const { storage_schema, queue_schema, track_schema } = Schemas;
  const toast = useToast();

  const { publicKey, signTransaction } = useWallet();
  const { connection } = useConnection();
  const [associatedTokenAddress, cacheAssociatedTokenAddress] = useState(null);

  const [LOOPS] = Constraints.pricingUnit;
  const [FILE, LINK] = Constraints.trackType;
  const [trackType, setTrackType] = useState(FILE);

  const [attested, setAttested] = useState(0);
  const [accountExists, cacheAccountExists] = useState(false);
  const handleAttestation = point => {
    log({ msg: 'Attestion count', count: attested });
    setAttested(attested + point);
  };

  const handleClose = () => {
    onClose();
    setFiles({});
    setShow(false);
  };

  const handleSelected = ({ target: { files } }) => {
    log({ msg: 'New File size', size: files[0] && files.size });
    setFiles(addNewFiles(files));
  };

  const addNewFiles = newFiles => {
    for (let file of newFiles) {
      files[file.name] = file;
    }
    return { ...files };
  };

  const removeFile = fileName => {
    delete files[fileName];
    setFiles({ ...files });
  };

  const handleUpload = () => {
    if (show) {
      log('No QDOT acount');
      toast({
        title: 'No QDOT Account Found',
        description:
          'Please use the `Open` button to open QDOT account or make track(s) free!',
        duration: 5000,
        isClosable: true,
        status: 'info',
        position: 'top',
      });
      return;
    }

    if (attested < Object.keys(files).length) {
      log('Incomplete Attestation');
      toast({
        title: 'Incomplete Attestation',
        description:
          'Please attest that you own the right to all the tracks or remove them',
        duration: 5000,
        isClosable: true,
        status: 'error',
        position: 'top',
      });
      return;
    }

    if (
      publicKey !== null &&
      queue[queue_schema.owner_wallet_address] !== MISTERY_WALLET &&
      queue[queue_schema.owner_wallet_address] !== publicKey.toBase58()
    ) {
      log('Updating collab');
      firebaseFacade.updateCollabs(queue);

      firebaseFacade.updateMonthlyCollabs(queue);
      log('Updated collab');
    }

    if (trackType === FILE) {
      uploadFiles();
    } else uploadLink();

    onClose();
    onUploadStart();
  };

  const createTrack = async (file, trackStorageRef, trackDbRef) => {
    try {
      let metadata = await parseMetadata(file);
      return processMetadata(metadata, trackStorageRef, trackDbRef, file.name);
    } catch (error) {
      log({
        msg: 'Error extracting metadata for',
        filename: file.name,
        error: error,
      });
      return Promise.reject(error);
    }
  };

  const parseMetadata = async file => {
    return await parseFile(file);
  };

  const processMetadata = async (
    metadata,
    trackStorageRef,
    trackDbRef,
    filename
  ) => {
    const { format, common } = metadata;
    const { picture: [{ data, format: pictureFormat, type }] = [{}] } = common;
    const track = { ...track_schema };

    track.created_on = Date.now();
    track[track_schema.track_address] = trackDbRef.id;
    track.loops = 0;

    track.title = (common && common.title) || 'Unknown track';
    track.artist = (common && common.artist) || 'Unknown artist';
    track.price = currentPrice[filename] || 0;
    track.pricing_unit = currentPriceUnit[filename] || LOOPS;

    track.duration = format.duration;
    track.queue_address = queue.queue_address;
    track.owner_wallet_address = (publicKey && publicKey.toBase58()) || null;

    try {
      track.track_uri = await firebaseFacade.createDownloadUrl(trackStorageRef);
    } catch (error) {
      log({ msg: 'Failed to create download url with ', error: error });
    }
    try {
      track.art_uri =
        (data &&
          (await firebaseFacade.uploadArt(track.track_address, data, {
            contentType: pictureFormat,
            type: type,
          }))) ||
        `${process.env.PUBLIC_URL}/logo-512x512-bg.png`;
    } catch (error) {
      log({ msg: 'Failed to upload art', error: error });
    }

    return firebaseFacade
      .insertDocument(trackDbRef, track)
      .then(_ => indexer.indexTrack(track));
  };

  const uploadFiles = async () => {
    const fileArray = convertObjectToArray(files);
    let successfulUpload = 0;

    for (let i = 0; i < fileArray.length; i++) {
      const trackRef = firebaseFacade.createTrackRef(queue);
      const fileRef = firebaseFacade.getFileRef(
        `${storage_schema.tracks}/${trackRef.id}`
      );
      const filename = fileArray[i].name;

      try {
        log({ msg: 'Uploading file', filename: filename });
        const uploadResult = await firebaseFacade.uploadFile(
          fileRef,
          fileArray[i]
        );
        if (uploadResult.error == null) {
          await createTrack(files[filename], uploadResult.ref, trackRef);
          ++successfulUpload;
        }

        log({
          msg: 'Uploaded file',
          filename: filename,
          count: successfulUpload,
        });
      } catch (error) {
        log({
          msg: 'Error uploading file',
          filename: filename,
          error: error,
        });
      }
    }

    firebaseFacade.updateMonthlyTrackCount(queue, successfulUpload);
    onFinished(successfulUpload);
    setFiles({});
  };

  const uploadLink = async () => {
    log('Uploading links');
  };

  const handleUploadBtnClick = () => {
    fileInputRef.current.click();
  };

  const handlePriceChange = (price, filename) => {
    setShow(price > 0 && !accountExists);
    const prices = { ...currentPrice };
    prices[filename] = price;
    setPrice(prices);
  };

  const handlePriceUnitChange = (unit, filename) => {
    const priceUnits = { ...currentPriceUnit };
    priceUnits[filename] = unit;
    setPriceUnit(priceUnits);
  };

  const handleCreateQdotAccount = useCallback(async () => {
    setOpening(true);
    const verdict = associatedTokenAddress
      ? await createAssociatedTokenAccount(
          connection,
          new PublicKey(associatedTokenAddress),
          publicKey,
          MINT_ADDRESS,
          publicKey,
          signTransaction
        )
      : false;

    if (verdict) {
      const description = `Your QDOT account has been created! Visit DexLab/Raydium to purchase some QDOTS! `;
      toast({
        title: 'Account Creation Succeeded',
        description: description,
        duration: 5000,
        isClosable: true,
        status: 'success',
        position: 'top',
      });
      setShow(false);
    } else {
      const description = `Unable to create an account. Please try again or use your preferred wallet app to add the Mint`;
      toast({
        title: 'Account Creation Failed',
        description: description,
        duration: 5000,
        isClosable: true,
        status: 'error',
        position: 'top',
      });
    }

    cacheAccountExists(verdict);
    setOpening(false);
  }, [associatedTokenAddress, publicKey]);

  const initilizeFinancials = useCallback(() => {
    if (publicKey !== null) {
      getAssociatedTokenAddress(MINT_ADDRESS, publicKey)
        .then(tokenAddr => {
          cacheAssociatedTokenAddress(tokenAddr.toBase58());
          return tokenAddr;
        })
        .then(tokenAddr =>
          getAccountInfo(connection, tokenAddr).then(_ => {
            cacheAccountExists(true);
          })
        )
        .catch(error => {
          const { message } = error;
          cacheAccountExists(!(message === 'TokenAccountNotFoundError'));
        });
    }
  });

  useEffect(initilizeFinancials, [publicKey]);

  log({
    accountExists: accountExists,
    agent: navigator.userAgent,
    component: 'track upload',
  });
  return (
    <Modal
      initialFocusRef={fileInputRef}
      isOpen={isOpen}
      onClose={onClose}
      size={['xl']}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalBody>
          <Center>
            <VStack>
              {/* <Select
                                m={2}
                                width={[125]}
                                placeholder={"Select track type"}
                                defaultValue={FILE}
                                onChange={handleTrackTypeChange}>
                                <option value={FILE}>FILE</option>
                                <option value={LINK}>LINK</option>
                            </Select> */}
              <Input
                display={'none'}
                size="lg"
                ref={fileInputRef}
                type={'file'}
                accept="audio/mpeg"
                multiple={true}
                onChange={handleSelected}
              />
              {trackType === FILE ? (
                <Button onClick={handleUploadBtnClick} mb="20px">
                  Select Files
                </Button>
              ) : (
                trackType === LINK && (
                  <Input
                    width={[250, 350, 500]}
                    ref={linkInputRef}
                    type={'url'}
                    placeholder={'Enter track link'}
                  />
                )
              )}

              {show && (
                <Button
                  onClick={handleCreateQdotAccount}
                  isLoading={opening}
                  isDisabled={opening}
                  mb="20px"
                >
                  Create QDOT Account
                </Button>
              )}
            </VStack>
          </Center>
          <SimpleGrid>
            {Object.keys(files).map(key => {
              let file = files[key];
              let fileName = file.name;
              return (
                <GridItem key={fileName}>
                  <TrackPreview
                    filename={fileName}
                    fileSize={convertBytesToMB(file.size)}
                    unit="MB"
                    onRemove={removeFile}
                    onPriceChange={handlePriceChange}
                    onUnitChange={handlePriceUnitChange}
                    onAttest={handleAttestation}
                  />
                </GridItem>
              );
            })}
          </SimpleGrid>
        </ModalBody>
        <ModalFooter>
          <Button
            isLoading={loading}
            isDisabled={loading}
            onClick={handleUpload}
            mr={3}
          >
            Upload
          </Button>
          <Button onClick={handleClose}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
